import React, { useState, useEffect } from "react";
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { Checkbox, Stack, TableCell, TextField, Typography } from '@mui/material';
import { PausedIndicator } from '../../../Components/PausedIndicator';
import { SkuFulfillmentTypeSelect } from "../components/SkuFulfillmentTypeSelect";
import { SkuOfferingStatusSelect } from '../components/SkuOfferingStatusSelect';
import { DistributorInMarketDatePicker } from "../components/DistributorInMarketDatePicker";
import { DistributorEndInMarketDatePicker } from "../components/DistributorEndInMarketDatePicker";
import { useProductSettingsContext } from "../context/ProductSettingsContext";
import PropTypes from 'prop-types';
import {
    enableInMarketDate,
    enableSeasonalForecast,
    SETTINGS_GRID_CELL_PADDING,
} from '../../../Utilities/settingsGridColumns';

const enablePauseIndicator = process.env.REACT_APP_ENABLE_PAUSE_INDICATOR === 'true';

export const DistributorTableRow = ({
    row,
    handleUpdateRow,
    onSaveProductSettingsDistributorOverride,
    isChanged,
}) => {
    const { onRowSelectClick, isRowSelected, productDefaults } = useProductSettingsContext();
    const [originalRow, setOriginalRow] = useState(row);
    const [localValues, setLocalValues] = useState({
        palletQuantity: row.palletQuantity,
        palletDivisor: row.palletDivisor,
        unitWeight: row.unitWeight,
    });

    // Update originalRow when row changes and contains complete data
    useEffect(() => {
        if (row && (
            !originalRow?.skuOfferingStatus && row.skuOfferingStatus ||
            !originalRow?.skuFulfillmentType && row.skuFulfillmentType ||
            JSON.stringify(row) !== JSON.stringify(originalRow)
        )) {
            setOriginalRow({ ...row });
        }
    }, [row, originalRow]);

    // Keep the locally-typed values in sync when the row changes from outside this
    // component (initial load, or the "Copy to Selected" bulk update).
    useEffect(() => {
        setLocalValues({
            palletQuantity: row.palletQuantity,
            palletDivisor: row.palletDivisor,
            unitWeight: row.unitWeight,
        });
    }, [row.palletQuantity, row.palletDivisor, row.unitWeight]);

    const commitField = async (field, value) => {
        const updatedRow = { ...row, [field]: value };
        await onSaveProductSettingsDistributorOverride(updatedRow, originalRow);
        await handleUpdateRow(updatedRow);
        setOriginalRow(updatedRow);
    };

    const onNumericBlurHandler = (field) => () => commitField(field, localValues[field]);

    const onNumericKeyPressHandler = (field) => async (event) => {
        if (event.key === "Enter") {
            await commitField(field, localValues[field]);
        }
    };

    const onCustomerPartNumberBlurHandler = async () => {
        await onSaveProductSettingsDistributorOverride(row, originalRow);
        await handleUpdateRow(row);
        setOriginalRow(row);
    };

    const onCustomerPartNumberKeyPressHandler = async (event) => {
        if (event.key === "Enter") {
            await onCustomerPartNumberBlurHandler();
        }
    };

    return (<React.Fragment>
        <TableCell sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
            <Stack direction='row' alignItems='center'>
                <Checkbox
                    id={row?.id}
                    color="primary"
                    disabled={row?.legal === false}
                    checked={isRowSelected(row)}
                    onChange={event => onRowSelectClick(row, event.target.checked)} />
                {enablePauseIndicator ? (row?.paused ? <PausedIndicator size={20} /> : <span style={{ width: 20, display: 'inline-block' }} />) : null}
                <Typography sx={{ minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere', marginLeft: 1 }}>
                    {`${row?.sapId} - ${row?.name}`}
                </Typography>
            </Stack>
        </TableCell>
        <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
            {row?.legal === false ? (<CloseIcon />) : <></>}
        </TableCell>
        <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
            <TextField
                placeholder="Default"
                value={localValues.palletQuantity}
                size="small"
                type="number"
                fullWidth
                disabled={row?.legal === false}
                onKeyPress={onNumericKeyPressHandler('palletQuantity')}
                onChange={(event) => {
                    if (!Number.isInteger(Number.parseFloat(event.target.value)) && event.target.value !== "") return;
                    setLocalValues(prev => ({ ...prev, palletQuantity: event.target.value }));
                }}
                onBlur={onNumericBlurHandler('palletQuantity')}
            />
        </TableCell>
        <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
            <TextField
                placeholder="Default"
                value={localValues.palletDivisor}
                size="small"
                type="number"
                fullWidth
                disabled={row?.legal === false}
                onKeyPress={onNumericKeyPressHandler('palletDivisor')}
                onChange={(event) => {
                    if (!Number.isInteger(Number.parseFloat(event.target.value)) && event.target.value !== "") return;
                    setLocalValues(prev => ({ ...prev, palletDivisor: event.target.value }));
                }}
                onBlur={onNumericBlurHandler('palletDivisor')}
            />
        </TableCell>
        <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
            <TextField
                placeholder="Default"
                value={localValues.unitWeight}
                size="small"
                type="number"
                fullWidth
                disabled={row?.legal === false}
                onKeyPress={onNumericKeyPressHandler('unitWeight')}
                onChange={(event) => {
                    if (Number.isNaN(event.target.value) && event.target.value !== "") return;
                    setLocalValues(prev => ({ ...prev, unitWeight: event.target.value }));
                }}
                onBlur={onNumericBlurHandler('unitWeight')}
            />
        </TableCell>
        <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
            <SkuOfferingStatusSelect
                row={row}
                originalRow={originalRow}
                handleUpdateRow={handleUpdateRow}
            />
        </TableCell>
        <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
            <SkuFulfillmentTypeSelect
                handleUpdateRow={handleUpdateRow}
                isChanged={isChanged}
                row={row}
                originalRow={originalRow} />
        </TableCell>
        {enableSeasonalForecast && (
            <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                {productDefaults?.seasonal ? <CheckIcon fontSize="small" color="success" titleAccess="Seasonal" /> : null}
            </TableCell>
        )}
        {enableInMarketDate && (
            <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                <DistributorInMarketDatePicker row={row} handleUpdateRow={handleUpdateRow} />
            </TableCell>
        )}
        {enableInMarketDate && enableSeasonalForecast && (
            <TableCell align="center" sx={{ padding: SETTINGS_GRID_CELL_PADDING }}>
                <DistributorEndInMarketDatePicker row={row} handleUpdateRow={handleUpdateRow} />
            </TableCell>
        )}
    </React.Fragment>)
}

DistributorTableRow.propTypes = {
    row: PropTypes.object,
    handleUpdateRow: PropTypes.func,
    onSaveProductSettingsDistributorOverride: PropTypes.func,
    isChanged: PropTypes.func,
}